import React, { useState, useEffect, useRef } from 'react';
import { adToBS, bsToAD, getDaysInBSMonth, NEPALI_MONTHS, formatNepaliDate } from '../utils/nepaliDate';

const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toAdString(adDate) {
  const y = adDate.getFullYear();
  const m = String(adDate.getMonth() + 1).padStart(2, '0');
  const d = String(adDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function NepaliDatePicker({ value, onChange, maxDate }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const todayBS = adToBS(new Date());
  const selectedBS = value ? adToBS(new Date(value + 'T00:00:00')) : null;

  const [viewYear, setViewYear]   = useState(selectedBS?.year  ?? todayBS?.year  ?? 2083);
  const [viewMonth, setViewMonth] = useState(selectedBS?.month ?? todayBS?.month ?? 0);

  // Sync view when value changes externally
  useEffect(() => {
    if (selectedBS) { setViewYear(selectedBS.year); setViewMonth(selectedBS.month); }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const daysInMonth = getDaysInBSMonth(viewYear, viewMonth);
  const firstDow    = bsToAD(viewYear, viewMonth, 1).getDay();

  const maxAD = maxDate ? new Date(maxDate + 'T23:59:59') : null;

  const handleDayClick = (day) => {
    const adDate = bsToAD(viewYear, viewMonth, day);
    if (maxAD && adDate > maxAD) return;
    onChange(toAdString(adDate));
    setOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const goToday = () => {
    const today = new Date();
    const bs = adToBS(today);
    if (bs) { setViewYear(bs.year); setViewMonth(bs.month); }
    onChange(toAdString(today));
    setOpen(false);
  };

  const displayText = value ? formatNepaliDate(new Date(value + 'T00:00:00')) : 'Select date';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input w-auto flex items-center gap-2 text-left min-w-[200px]"
      >
        <span className="flex-1 text-gray-800 text-sm">{displayText}</span>
        <span className="text-gray-400 text-base">📅</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 rounded-xl shadow-2xl p-3 w-72 left-0 sm:left-auto" style={{ background:'var(--bg-card)', border:'1px solid var(--border-strong)', maxWidth:'calc(100vw - 24px)' }}>
          {/* Month / Year navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={prevMonth}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 font-bold text-lg"
            >‹</button>
            <span className="font-semibold text-gray-800 text-sm">
              {NEPALI_MONTHS[viewMonth]} {viewYear} B.S.
            </span>
            <button
              onClick={nextMonth}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 font-bold text-lg"
            >›</button>
          </div>

          {/* Week day headers */}
          <div className="grid grid-cols-7 mb-1">
            {WEEK_DAYS.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {Array(firstDow).fill(null).map((_, i) => <div key={`blank-${i}`} />)}
            {Array(daysInMonth).fill(null).map((_, i) => {
              const day = i + 1;
              const adDate = bsToAD(viewYear, viewMonth, day);
              const isSelected  = selectedBS && selectedBS.year === viewYear && selectedBS.month === viewMonth && selectedBS.day === day;
              const isToday     = todayBS && todayBS.year === viewYear && todayBS.month === viewMonth && todayBS.day === day;
              const isDisabled  = maxAD && adDate > maxAD;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => !isDisabled && handleDayClick(day)}
                  disabled={isDisabled}
                  className={`text-center py-1.5 rounded-lg text-sm transition-colors
                    ${isSelected  ? 'bg-blue-600 text-white font-bold' :
                      isToday     ? 'bg-blue-50 text-blue-600 font-semibold ring-1 ring-blue-300' :
                      isDisabled  ? 'text-gray-300 cursor-not-allowed' :
                                    'hover:bg-gray-100 text-gray-700'}`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Close
            </button>
            <button
              type="button"
              onClick={goToday}
              className="text-xs text-blue-600 font-medium hover:underline"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
